import React, { useState, useRef, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, useWindowDimensions, BackHandler, Alert, Animated, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { StackScreenProps } from '@react-navigation/stack';
import { RootStackParamList } from '../navigation/types';
import { useAuthStore } from '../store/useAuthStore';
import { useActivityStore } from '../store/useActivityStore';
import { useAccessibilityStore } from '../store/useAccessibilityStore';
import { useAccommodationStore } from '../store/useAccommodationStore';
import { useStrokeStore } from '../store/useStrokeStore';
import { StrokeSyncService } from '../services/StrokeSyncService';
import { TaskCompletionService } from '../services/TaskCompletionService';
import { ActivityRunner } from '../components/ActivityRunner';
import { AIFeedbackCard } from '../components/AIFeedbackCard';
import { TeacherFeedbackOverlay } from '../components/TeacherFeedbackOverlay';
import { NotificationPanel } from '../components/NotificationPanel';
import { NotificationDetail } from '../components/NotificationDetail';
import { Notification } from '../store/NotificationStore';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { FriendlyButton } from '../components/FriendlyButton';
import { AudioPromptButton } from '../components/AudioPromptButton';
import { ChildFriendlyBackground } from '../components/ChildFriendlyBackground';
import { StudentLessonHeader } from '../components/StudentLessonHeader';
import { TeacherActivityFeedbackModal, FeedbackSubmissionPayload } from '../components/TeacherActivityFeedbackModal';
import { captureScreenSnapshot } from '../utils/screenshotCapture';
import { useHoldToConfirm } from '../hooks/useHoldToConfirm';
import { EvaluatorRouter, EvaluationResult } from '@eduwrite/shared';

const evaluatorRouter = new EvaluatorRouter();

function getCelebrationCopy(type: string, score: number): string {
    if (score < 50) return 'Keep practicing! You\'ll get there!';
    if (score < 75) return 'Good effort!';
    if (score < 90) return 'Great job!';
    switch (type) {
        case 'maths': return 'Maths Whiz!';
        case 'spelling':
        case 'common_exception_words': return 'Spelling Star!';
        case 'sentence_building': return 'Sentence Superstar!';
        case 'dictation': return 'Dictation Champion!';
        case 'letter_tracing':
        case 'letter_formation': return 'Neatness Ninja!';
        case 'copy_writing': return 'Copycat Pro!';
        case 'reading_comprehension': return 'Reading Rockstar!';
        default: return 'Fantastic work!';
    }
}

type Props = StackScreenProps<RootStackParamList, 'ActiveLesson'>;

export function ActiveLessonScreen({ navigation }: Props) {
    const { width, height } = useWindowDimensions();
    const isLandscape = width > height;

    const { studentName, leaveClass, switchStudent, studentId, classId, keyStage } = useAuthStore();
    const {
        activities,
        currentActivityIndex,
        pendingActivities,
        startPendingActivity,
        resetActivities,
        addCompletedTask,
        nextActivity
    } = useActivityStore();

    const { isReady, setReady } = useAuthStore();


    const { completedStrokes, structuredData, clearStrokes } = useStrokeStore();

    const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [appState, setAppState] = useState<'ACTIVE' | 'ANALYZING'>('ACTIVE');
  
    const [aiResult, setAiResult] = useState<unknown>(null);
    const [isNotifOpen, setIsNotifOpen] = useState(false);
    const [selectedNotif, setSelectedNotif] = useState<Notification | null>(null);

    // WP-100: Teacher Feedback State & Handlers
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [screenshotDataUri, setScreenshotDataUri] = useState<string | null>(null);

    const handleOpenFeedback = async () => {
        try {
            const uri = await captureScreenSnapshot();
            setScreenshotDataUri(uri);
        } catch {
            setScreenshotDataUri(null);
        }
        setIsFeedbackModalOpen(true);
    };

    const handleFeedbackSubmit = async (payload: FeedbackSubmissionPayload) => {
        const serverUrl = useAuthStore.getState().serverUrl || 'http://localhost:4005';
        const apiBase = String(serverUrl).replace(/\/+$/, '');
        const res = await fetch(`${apiBase}/api/feedback/activity`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        if (!res.ok) {
            const errJson = await res.json().catch(() => ({}));
            throw new Error(errJson.error || 'Failed to submit feedback');
        }
    };

    const handleSwitchProfile = () => {
        const currentClassId = classId;
        const currentServerUrl = StrokeSyncService.getServerUrl();
        StrokeSyncService.stopSync();
        resetActivities();
        useAccessibilityStore.getState().clearLessonTextToSpeechOverride();
        useAccommodationStore.getState().reset();
        switchStudent(); // preserves deviceToken, classId, serverUrl
        if (currentClassId && currentServerUrl) {
            navigation.replace('RosterPick', { classId: currentClassId, serverUrl: currentServerUrl });
        } else {
            navigation.replace('Home');
        }
    };

    const handleExit = () => {
        StrokeSyncService.stopSync();
        resetActivities();
        useAccessibilityStore.getState().clearLessonTextToSpeechOverride();
        useAccommodationStore.getState().reset();
        leaveClass();
        navigation.replace('Home');
    };

    // WP-033: both Exit buttons (waiting-screen + active-lesson header) require
    // a sustained press-and-hold (~1.2s) to activate. A quick tap does nothing —
    // young children tapping accidentally won't leave the session. While held,
    // a progress bar fills and "Keep holding to leave…" text appears.
    const exitHold = useHoldToConfirm({
        holdMs: 1200,
        onConfirm: handleExit,
    });

    const handleBackPress = () => {
        if (activities.length === 0) return false;
        Alert.alert(
            'Go back to task list?',
            'You can come back to your work later!',
            [
                { text: 'Stay here', style: 'cancel' },
                { text: 'Go back', onPress: () => navigation.goBack() },
            ],
        );
        return true;
    };

    useEffect(() => {
        if (Platform.OS === 'web') return;
        const subscription = BackHandler.addEventListener('hardwareBackPress', handleBackPress);
        return () => subscription.remove();
    }, [activities.length]);

    const handleActivityComplete = async () => {
        setAppState('ANALYZING');
        const currentActivity = activities[currentActivityIndex];
        const latestStructured = useStrokeStore.getState().structuredData as any;

        const evaluableSubmission = {
            id: `local-${Date.now()}`,
            activityId: currentActivity.id,
            studentId: studentId || 'unknown',
            classId: classId || 'unknown',
            strokes: completedStrokes,
            structuredData: latestStructured,
            finalText: typeof latestStructured?.answer === 'string' ? latestStructured.answer : undefined,
            createdAt: Date.now(),
        };

        const evaluableActivity = {
            id: currentActivity.id,
            type: currentActivity.type,
            mechanic: currentActivity.mechanic,
            config: currentActivity.config || {},
        };

        try {
            const rawResult = await evaluatorRouter.evaluate(evaluableSubmission, evaluableActivity);
            const celebration = getCelebrationCopy(currentActivity.type, rawResult.score);
            const result: EvaluationResult & { tipsTitle?: string; buttonText?: string } = {
                ...rawResult,
                feedback: `${celebration} ${rawResult.feedback}`.trim(),
                tipsTitle: 'Coaching Tips:',
                buttonText: 'NEXT! \u2794',
            };

            setAiResult(result as any);
            setAppState('ACTIVE');

            // Check if there are more tasks in the current batch
            const isLastOne = currentActivityIndex >= activities.length - 1;
            const shouldHideTimers = useAccommodationStore.getState().shouldHideTimers();

            // If it's NOT the last one and timers are not suppressed by accommodation, wait briefly then auto-dismiss
            if (!isLastOne && !shouldHideTimers) {
                autoAdvanceTimerRef.current = setTimeout(() => {
                    autoAdvanceTimerRef.current = null;
                    handleAutoAdvance(result);
                }, 1500); // 1.5s delay to see the celebration or score
            }
        } catch (err) {
            console.error('Activity evaluation failed:', err);
            setAppState('ACTIVE');
            const fallbackResult = {
                score: 100,
                maxScore: 100,
                isCorrect: true,
                feedback: 'Great job!',
                buttonText: 'NEXT! \u2794',
            };
            const isLastOne = currentActivityIndex >= activities.length - 1;
            const shouldHideTimers = useAccommodationStore.getState().shouldHideTimers();
            if (!isLastOne && !shouldHideTimers) {
                autoAdvanceTimerRef.current = setTimeout(() => {
                    autoAdvanceTimerRef.current = null;
                    handleAutoAdvance(fallbackResult);
                }, 1500);
            } else {
                setAiResult(fallbackResult as any);
            }
        }
    };

    const handleAutoAdvance = (result: unknown) => {
        const currentActivity = activities[currentActivityIndex];
        addCompletedTask({
            activity: currentActivity,
            strokes: useStrokeStore.getState().completedStrokes,
            structuredData: useStrokeStore.getState().structuredData,
            score: (result as any).score
        });

        const assignmentId = (currentActivity as any).assignmentId;
        const serverUrl = StrokeSyncService.getServerUrl();
        if (studentId && assignmentId && serverUrl) {
            TaskCompletionService.markTaskComplete(studentId, assignmentId, serverUrl, (result as any).score).catch(console.error);
        }

        setAiResult(null);
        clearStrokes();
        nextActivity();
    };

    const handleFeedbackDismiss = () => {
        if (autoAdvanceTimerRef.current) {
            clearTimeout(autoAdvanceTimerRef.current);
            autoAdvanceTimerRef.current = null;
        }
        const currentActivity = activities[currentActivityIndex];
        addCompletedTask({
            activity: currentActivity,
            strokes: completedStrokes,
            structuredData: structuredData,
            score: (aiResult as any).score
        });

        const assignmentId = (currentActivity as any).assignmentId;
        const serverUrl = StrokeSyncService.getServerUrl();
        if (studentId && assignmentId && serverUrl) {
            TaskCompletionService.markTaskComplete(studentId, assignmentId, serverUrl, (aiResult as any).score).catch(console.error);
        }

        setAiResult(null);
        clearStrokes();

        const isAtEnd = currentActivityIndex >= activities.length - 1;
        if (isAtEnd) {
            if (pendingActivities.length > 0) {
                startPendingActivity();
            } else {
                navigation.replace('LessonComplete');
            }
        } else {
            nextActivity();
        }
    };

    const currentActivity = activities[currentActivityIndex];

    const effectiveActivity = useMemo(() => {
        if (!currentActivity) return currentActivity;
        const accommodation = useAccommodationStore.getState();
        const effectiveTier = accommodation.getEffectiveTier(currentActivity.tier as string);
        const speechAllowed = accommodation.isSpeechInputAllowed();

        let updatedConfig = currentActivity.config;
        if (speechAllowed && updatedConfig) {
            const currentModes = Array.isArray(updatedConfig.allowedInputModes) ? updatedConfig.allowedInputModes : [];
            if (!currentModes.includes('speech')) {
                updatedConfig = {
                    ...updatedConfig,
                    allowedInputModes: [...currentModes, 'speech'],
                };
            }
        }

        return {
            ...currentActivity,
            tier: effectiveTier,
            config: updatedConfig,
        };
    }, [currentActivity]);

    const waitBounceAnim = useRef(new Animated.Value(0)).current;
    useEffect(() => {
        if (activities.length > 0) return;
        const anim = Animated.loop(
            Animated.sequence([
                Animated.timing(waitBounceAnim, { toValue: 1, duration: 800, useNativeDriver: true }),
                Animated.timing(waitBounceAnim, { toValue: 0, duration: 800, useNativeDriver: true }),
            ])
        );
        anim.start();
        return () => anim.stop();
    }, [activities.length, waitBounceAnim]);

    if (activities.length === 0) {
        const markReady = () => {
            if (isReady) return;
            setReady(true);
            StrokeSyncService.sendReady(true);
        };

        const mascotScale = waitBounceAnim.interpolate({
            inputRange: [0, 1],
            outputRange: [1, 1.15],
        });

        return (
            <ChildFriendlyBackground variant="warm">
                <SafeAreaView style={styles.waitingContainer}>
                    <View style={styles.waitingHeader}>
                        <Text style={styles.headerText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{studentName}</Text>
                        <View style={styles.headerActions}>
                            <TouchableOpacity
                                onPressIn={exitHold.pressIn}
                                onPressOut={exitHold.pressOut}
                                activeOpacity={0.6}
                                accessibilityLabel="Exit"
                                accessibilityRole="button"
                                accessibilityHint="Hold to leave the class and return to the home screen"
                                style={styles.exitButton}
                            >
                                <Text style={styles.leaveText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Exit</Text>
                                {exitHold.isConfirming ? (
                                    <Text style={styles.holdHint} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Keep holding to leave…</Text>
                                ) : null}
                                {exitHold.isConfirming ? (
                                    <Animated.View style={[styles.holdBar, { width: exitHold.progress.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }) }]} />
                                ) : null}
                            </TouchableOpacity>
                        </View>
                    </View>
                    <View style={styles.center}>
                        <View style={styles.waitCard}>
                            <Animated.View style={{ transform: [{ scale: mascotScale }] }}>
                                <Text style={styles.waitEmoji} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{isReady ? '🎉' : '👋'}</Text>
                            </Animated.View>

                            <View style={styles.liveBadge}>
                                <View style={styles.liveDot} />
                                <Text style={styles.liveBadgeText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Connected to Class</Text>
                            </View>

                            <Text style={styles.waitTitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                {isReady ? "You're all set!" : "Welcome, " + (studentName?.split(' ')[0] || 'Student') + "!"}
                            </Text>
                            <Text style={[styles.waitSubtitle, isLandscape && styles.landscapeSubtitle]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                {isReady
                                    ? "We're waiting for the teacher to start the activity."
                                    : "Tap the button below once you're ready to start."}
                            </Text>

                            <AudioPromptButton
                                text={isReady ? "We're waiting for the teacher to start the activity." : "Welcome! Tap the button below once you're ready to start."}
                                autoPlay={false}
                            />

                            <View style={{ height: 16 }} />

                            <FriendlyButton
                                variant={isReady ? 'primary' : 'secondary'}
                                size="large"
                                onPress={markReady}
                                accessibilityLabel="I'm Ready"
                                accessibilityHint="Tell your teacher you are ready to start"
                            >
                                {isReady ? "I'M READY! ✓" : "I'M READY!"}
                            </FriendlyButton>

                            {!isReady && (
                                <Text style={styles.waitStatus} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                    Ready to join your teacher?
                                </Text>
                            )}
                        </View>
                    </View>
                </SafeAreaView>
            </ChildFriendlyBackground>
        );
    }

    return (
        <SafeAreaView style={styles.container} accessibilityLabel="Active lesson screen" accessibilityRole="none">
            <StudentLessonHeader
                activity={effectiveActivity}
                classTextToSpeechPolicy={effectiveActivity.classAccessibilitySettings?.textToSpeechPolicy}
                stage={keyStage ?? undefined}
                studentName={studentName}
                taskIndex={currentActivityIndex + 1}
                taskCount={activities.length}
                onBack={handleBackPress}
                onNotifications={() => setIsNotifOpen(true)}
                onNext={pendingActivities.length > 0 ? () => { clearStrokes(); startPendingActivity(); } : undefined}
                onSwitchStudent={handleSwitchProfile}
                onExitPressIn={exitHold.pressIn}
                onExitPressOut={exitHold.pressOut}
                onFeedbackPress={handleOpenFeedback}
            />

            <View style={styles.activityContainer}>
                <ErrorBoundary>
                    <ActivityRunner key={effectiveActivity.assignmentId || effectiveActivity.id || `act-${currentActivityIndex}`} activity={effectiveActivity} onComplete={handleActivityComplete} />
                </ErrorBoundary>
            </View>

            {appState === 'ANALYZING' && (
                <View style={styles.overlay}>
                    <View style={styles.analyzingCard}>
                        <ActivityIndicator size="large" color="#22c55e" />
                        <Text style={styles.analyzingText} accessibilityLiveRegion="polite" allowFontScaling={true} maxFontSizeMultiplier={1.5}>AI is checking your neatness...</Text>
                        <Text style={styles.analyzingSubtext} accessibilityLiveRegion="polite" allowFontScaling={true} maxFontSizeMultiplier={1.5}>Hang tight!</Text>
                    </View>
                </View>
            )}

            {!!aiResult && (
                <AIFeedbackCard
                    score={(aiResult as any).score}
                    feedback={(aiResult as any).feedback}
                    suggestions={(aiResult as any).suggestions}
                    label={(aiResult as any).label}
                    tipsTitle={(aiResult as any).tipsTitle}
                    buttonText={(aiResult as any).buttonText}
                    onDismiss={handleFeedbackDismiss}
                />
            )}

            <TeacherFeedbackOverlay />

            {isNotifOpen && (
                <NotificationPanel
                    onClose={() => setIsNotifOpen(false)}
                    onSelectNotification={(n) => setSelectedNotif(n)}
                />
            )}
            {selectedNotif && (
                <NotificationDetail
                    notification={selectedNotif}
                    onClose={() => setSelectedNotif(null)}
                />
            )}

            <TeacherActivityFeedbackModal
                visible={isFeedbackModalOpen}
                activity={currentActivity}
                classId={classId || undefined}
                stepContext={{
                    stepIndex: currentActivityIndex,
                    totalSteps: activities.length,
                    label: currentActivity?.title,
                }}
                screenshotUri={screenshotDataUri}
                onSubmit={handleFeedbackSubmit}
                onClose={() => setIsFeedbackModalOpen(false)}
            />
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: '#F8FAFC' },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 12,
        backgroundColor: '#FFFFFF',
        borderBottomWidth: 1,
        borderBottomColor: '#E2E8F0',
    },
    headerText: { fontSize: 16, fontWeight: 'bold', color: '#1E293B' },
    headerLeft: { flexDirection: 'row', alignItems: 'center', gap: 12 },
    backButton: { paddingHorizontal: 8, paddingVertical: 4, minHeight: 44, justifyContent: 'center' },
    backText: { color: '#6366F1', fontWeight: '700', fontSize: 16 },
    headerActions: { flexDirection: 'row', alignItems: 'center', gap: 12 },
    switchText: { color: '#64748B', fontWeight: '600' },
    leaveText: { color: '#EF4444', fontWeight: 'bold' },
    // WP-033: press-and-hold exit styles — child-friendly "Keep holding" hint
    // + a progress bar that fills as the child holds.
    holdHint: { fontSize: 12, color: '#F59E0B', marginTop: 2, textAlign: 'center' },
    holdBar: { height: 4, backgroundColor: '#F59E0B', borderRadius: 2, marginTop: 4 },
    pendingBadge: {
        backgroundColor: '#22c55e',
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderRadius: 20,
        shadowColor: '#22c55e',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 4,
        elevation: 3,
    },
    pendingBadgeText: { color: '#FFFFFF', fontSize: 13, fontWeight: '900' },
    activityContainer: { flex: 1, minHeight: 0 },
    center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 },
    waitTitle: { fontSize: 26, fontWeight: 'bold', color: '#1E293B', marginBottom: 8, textAlign: 'center' },
    waitSubtitle: { fontSize: 16, color: '#64748B', textAlign: 'center' },
    overlay: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'rgba(15, 23, 42, 0.6)',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100,
    },
    analyzingCard: {
        backgroundColor: '#FFFFFF',
        padding: 32,
        borderRadius: 24,
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.2,
        shadowRadius: 20,
        elevation: 10,
    },
    analyzingText: { fontSize: 18, fontWeight: 'bold', color: '#1E293B', marginTop: 16 },
    analyzingSubtext: { fontSize: 14, color: '#64748B', marginTop: 8 },
    waitCard: {
        backgroundColor: '#FFF7E6',
        padding: 30,
        borderRadius: 32,
        alignItems: 'center',
        width: '90%',
        maxWidth: 600,
        shadowColor: '#FAAD14',
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.2,
        shadowRadius: 20,
        elevation: 5,
        borderWidth: 2,
        borderColor: '#FFD8BF',
    },
    waitEmoji: { fontSize: 64, marginBottom: 16 },
    landscapeSubtitle: { fontSize: 14, maxWidth: 400 },
    waitStatus: {
        marginTop: 20,
        color: '#94A3B8',
        fontSize: 14,
        fontWeight: '600',
    },
    waitingContainer: { flex: 1 },
    waitingHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 12,
        backgroundColor: 'rgba(255, 255, 255, 0.85)',
        borderBottomWidth: 1.5,
        borderBottomColor: '#FFD8BF',
    },
    exitButton: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 12,
        minHeight: 56,
        minWidth: 56,
    },
    liveBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F0FDF4',
        paddingHorizontal: 12,
        paddingVertical: 4,
        borderRadius: 16,
        borderWidth: 1.5,
        borderColor: '#86EFAC',
        gap: 6,
        marginBottom: 12,
    },
    liveDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: '#22C55E',
    },
    liveBadgeText: {
        color: '#15803D',
        fontSize: 12,
        fontWeight: '800',
    },
});
